import {
  Classes as BlueprintClasses,
  PopoverPosition as BlueprintPopoverPosition,
} from '@blueprintjs/core';
import { ChartCustom, Reset } from '@carbon/icons-react';
import * as React from 'react';

import { useVoiceboxPanelData } from 'bones/components/molecules/Voicebox/context/VoiceboxPanelData';
import { useVoiceboxSchema } from 'bones/components/molecules/Voicebox/context/VoiceboxSchema';
import { useParsedChartSpecFromMessage } from 'bones/components/molecules/Voicebox/hooks/useParsedChartSpecFromMessage';
import { VoiceboxPanelChartDialog } from 'bones/components/molecules/Voicebox/panel/VoiceboxPanelChartDialog';
import { VoiceboxResponseMessage } from 'bones/constants/voicebox/message';
import { Button } from 'bones/design-system/components/Button';
import { Chart } from 'bones/design-system/components/Chart';
import { Tooltip } from 'bones/design-system/components/Tooltip';
import { getChartCustomizationsKey } from 'bones/utils/voicebox/getChartCustomizationsKey';

export interface VoiceboxPanelChartProps {
  message: VoiceboxResponseMessage;
}

export const VoiceboxPanelChart: React.VFC<VoiceboxPanelChartProps> = ({
  message,
}) => {
  const { copy, testIds } = useVoiceboxSchema();
  const { chartDataByMessageId, requestChart } = useVoiceboxPanelData();

  const messageChartData = chartDataByMessageId[message.messageId];
  const chartData =
    messageChartData?.chartDataByKey[messageChartData?.key || ''];
  const { error: chartError, spec: chartSpec } = useParsedChartSpecFromMessage({
    actions: chartData?.data || undefined,
  });
  const customizations =
    chartData?.customizations || /* istanbul ignore next */ [];

  const [hasValidChart, setHasValidChart] = React.useState<boolean | undefined>(
    undefined
  );

  const [isCustomizeDialogOpen, setIsCustomizeDialogOpen] =
    React.useState(false);

  return (
    <>
      <div
        className="vb-voicebox-panel-chart"
        data-testid={testIds.panel.messageSummary.chart.container}
      >
        {chartData?.loading ? (
          <div
            className={BlueprintClasses.SKELETON}
            data-testid={testIds.panel.messageSummary.chart.loading}
          />
        ) : (
          <>
            {(chartData && !chartData.data) ||
            chartError ||
            hasValidChart === false ? (
              <div className="vb-voicebox-panel-chart-error">
                {copy.panel.messageSummary.chart.error}
                <Button
                  leftIcon={Reset}
                  onClick={() => {
                    requestChart(
                      message.messageId,
                      chartData?.customizations,
                      true
                    );
                  }}
                  size="sm"
                  testId={testIds.panel.messageSummary.chart.retry}
                  variant="tertiary-gray"
                >
                  {copy.panel.messageSummary.chart.retry}
                </Button>
              </div>
            ) : null}

            {chartSpec ? (
              <>
                {hasValidChart ? (
                  <div className="vb-voicebox-panel-chart-actions">
                    <Tooltip
                      description={copy.panel.messageSummary.chart.customize}
                      position={BlueprintPopoverPosition.BOTTOM}
                    >
                      <Button
                        leftIcon={ChartCustom}
                        onClick={() => {
                          setIsCustomizeDialogOpen(true);
                        }}
                        size="sm"
                        testId={testIds.panel.messageSummary.chart.customize}
                        variant="ghost-gray"
                      />
                    </Tooltip>
                  </div>
                ) : null}
                <Chart
                  onValidateChart={setHasValidChart}
                  showActions={false}
                  spec={chartSpec}
                  testId={testIds.panel.messageSummary.chart.chart(
                    getChartCustomizationsKey(customizations),
                    customizations.length
                  )}
                />
              </>
            ) : null}
          </>
        )}
      </div>

      <VoiceboxPanelChartDialog
        isOpen={isCustomizeDialogOpen}
        message={message}
        onClose={() => {
          setIsCustomizeDialogOpen(false);
        }}
      />
    </>
  );
};
